import { UsersIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';

import { Loader } from '@/components/core/Loader';
import { Panel, PanelHeader } from '@/components/core/Panel';
import { Tabs, TabsList, TabsTrigger } from '@/components/core/Tabs';
import { useOrganization } from '@/hooks/useOrganization';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';

import { BusinessPartners } from './BusinessPartners';
import { ContactsTab, contactsTabForHash, setLocationHash } from './contactsTab';
import { Invitations } from './Invitations';
import { OrganizationSearch } from './OrganizationSearch';
import { useMemberCommunities } from './organizationSearch.queries';

const currentHash = (): string => {
    try {
        return String(getInjector().get('$location').hash() ?? '');
    } catch {
        return window.location.hash.replace(/^#/, '');
    }
};

const ContactsPage = () => {
    const { t } = useTranslation();
    const { organization } = useOrganization();
    const communityManager = Boolean(organization?.communityManager);
    const communities = useMemberCommunities(communityManager);
    const waitingForCommunities = communityManager && communities.isLoading;
    const allowCommunities = communityManager && (communities.data?.length ?? 0) > 0;
    const [tab, setTab] = useState<ContactsTab>(() => contactsTabForHash(currentHash()) ?? 'partners');

    useEffect(() => {
        const sync = () => {
            const next = contactsTabForHash(currentHash());
            if (next) {
                setTab(next);
            }
        };
        window.addEventListener('hashchange', sync);
        let unlisten: (() => void) | undefined;
        try {
            unlisten = getRootScope(getInjector()).$on('$locationChangeSuccess', sync);
        } catch {
            // The Angular shell is absent in tests.
        }
        return () => {
            window.removeEventListener('hashchange', sync);
            unlisten?.();
        };
    }, []);

    useEffect(() => {
        if (!waitingForCommunities && tab === 'community' && !allowCommunities) {
            setTab('partners');
            setLocationHash('partners');
        }
    }, [allowCommunities, tab, waitingForCommunities]);

    const openTab = (next: string) => {
        if (next !== 'partners' && next !== 'community' && next !== 'invitations' && next !== 'search') {
            return;
        }
        if (next === 'community' && !allowCommunities) {
            return;
        }
        setTab(next);
        setLocationHash(next);
    };

    return (
        <Panel>
            <div id="businessRelationshipManager">
                {waitingForCommunities ? (
                    <PanelHeader icon={UsersIcon} title={t('MEMBERS')} actions={<Loader />} />
                ) : (
                    <Tabs value={tab} onValueChange={openTab}>
                        <PanelHeader
                            icon={UsersIcon}
                            title={t('MEMBERS')}
                            actions={(
                                <TabsList
                                    id="membersTab"
                                    className="tw-h-auto tw-flex-wrap tw-justify-end tw-bg-fill-secondary tw-p-1"
                                >
                                    <TabsTrigger value="partners">{t('BUSINESS_PARTNER')}</TabsTrigger>
                                    {allowCommunities && (
                                        <TabsTrigger value="community">{t('COMMUNITIES')}</TabsTrigger>
                                    )}
                                    <TabsTrigger value="invitations">{t('INVITATIONS')}</TabsTrigger>
                                    <TabsTrigger value="search">{t('SEARCH_BUSINESSES')}</TabsTrigger>
                                </TabsList>
                            )}
                        />
                    </Tabs>
                )}
                <div className="tw-mt-4">
                    {waitingForCommunities ? <Loader /> : null}
                    {!waitingForCommunities && tab === 'partners' && <BusinessPartners />}
                    {!waitingForCommunities && tab === 'community' && allowCommunities && (
                        <OrganizationSearch mode="community" />
                    )}
                    {!waitingForCommunities && tab === 'invitations' && <Invitations />}
                    {!waitingForCommunities && tab === 'search' && <OrganizationSearch mode="search" />}
                </div>
            </div>
        </Panel>
    );
};

export { ContactsPage };
